import {
  ReportSection,
  ReportSectionConfigurationBase,
  ReportSectionConfigurationControlType,
  ReportSectionConfigurationWithOrderImpl,
  ReportType,
  ReportViewModelSection
} from '@capital-access/reporting/common';
import { ItineraryActivityHistoryConfiguration } from './itinerary-activity-history-configuration';
import { ItineraryParticipantBioConfiguration } from './itinerary-participant-bio-configuration';
import { ItineraryActivity, ItineraryParticipantInfo } from './itinerary-participant-info.model';
import { ItineraryPeerOwnershipConfiguration } from './itinerary-peer-ownership-configuration';

export const ITINERARY_PARTICIPANT_INFO_MAX_GRID_UNITS = 21.5;

export class ItineraryParticipantInfoConfiguration extends ReportSectionConfigurationBase {
  typeName = 'ItineraryParticipantInfoConfiguration';
  nodataLocalizationKeyName = 'participantsInfo';
  itineraryReportingFeatureToggle = 'hide-configurable-itinerary-reporting-new-data-points-ISD-17499';
  readonly reportSection = ReportSection.ItineraryParticipantInfo;
  readonly applicableReportTypes = [ReportType.Itinerary];
  readonly reportViewModelSections = [ReportViewModelSection.Itinerary_ParticipantInfo];
  override maxGridUnitsAllowed = ITINERARY_PARTICIPANT_INFO_MAX_GRID_UNITS;

  calculateTotalSelectedUnits(): number {
    return this.availableDataSections
      .filter(section => section.isSelected)
      .reduce((sum, section) => {
        const baseUnits = section.unit || 0;
        const nestedUnits = section.subSections
          ? section.subSections.filter(sub => sub.isSelected).reduce((subSum, sub) => subSum + (sub.unit || 0), 0)
          : 0;
        return sum + baseUnits + nestedUnits;
      }, 0);
  }

  isMaxUnitsLimitReached(): boolean {
    return this.calculateTotalSelectedUnits() >= ITINERARY_PARTICIPANT_INFO_MAX_GRID_UNITS;
  }

  is16UnitLimitReached(): boolean {
    return this.calculateTotalSelectedUnits() >= 16;
  }

  shouldDisableDataSection(
    section: { name?: string; unit?: number; isSelected?: boolean },
    sumWeight: number
  ): boolean {
    if (section.name === 'participantsInfoInstitutionDetailsType' || section.name === 'externalParticipantJobRole') {
      return false;
    }

    if (!section.unit) {
      return false;
    }

    if (section.isSelected) {
      return false;
    }

    return sumWeight + section.unit > 16;
  }

  shouldDisableSubSection(subSection: { unit?: number; isSelected?: boolean }, sumWeight: number): boolean {
    if (!subSection.unit) {
      return false;
    }

    if (subSection.isSelected) {
      return false;
    }

    return sumWeight + subSection.unit > 16;
  }

  minSize = 3;
  maxSize = 3;
  defaultSize = 3;
  override disabledLayoutWidthControl = true;
  name = 'participantsInfo';
  displayName = 'Participants Info';
  type = ReportSectionConfigurationControlType.Text;
  isSelected = true;
  canConfigure = false;
  required = true;
  property = 'participantsInfo';
  override disableSectionSelection = true;
  override disableSectionReordering = true;
  override readonly nestedItems = [
    {
      name: 'peerOwnership',
      featureToggle: this.itineraryReportingFeatureToggle,
      nestedItemSectionConfiguration: new ReportSectionConfigurationWithOrderImpl(
        1,
        true,
        ItineraryPeerOwnershipConfiguration
      )
    },
    {
      name: 'activityHistory',
      featureToggle: this.itineraryReportingFeatureToggle,
      nestedItemSectionConfiguration: new ReportSectionConfigurationWithOrderImpl(
        2,
        true,
        ItineraryActivityHistoryConfiguration
      )
    },
    {
      name: 'participantBio',
      featureToggle: this.itineraryReportingFeatureToggle,
      nestedItemSectionConfiguration: new ReportSectionConfigurationWithOrderImpl(
        3,
        true,
        ItineraryParticipantBioConfiguration
      )
    }
  ];

  readonly availableDataSections = [
    {
      name: 'participantsInfoInstitutionDetailsName',
      displayName: 'Institution',
      isSelected: true,
      canConfigure: false,
      isStatic: true,
      unit: 2.5,
      property: 'institutionName'
    },
    {
      name: 'participantsInfoInstitutionDetailsType',
      displayName: 'Institution Type',
      isSelected: true,
      canConfigure: true,
      unit: 0,
      property: 'institutionType'
    },
    {
      name: 'externalParticipants',
      displayName: 'External Participants',
      isSelected: true,
      canConfigure: false,
      isStatic: true,
      unit: 2.5,
      property: 'externalParticipants'
    },
    {
      name: 'externalParticipantJobRole',
      displayName: 'External Participant Job Role',
      isSelected: true,
      canConfigure: true,
      unit: 0,
      property: 'externalParticipantJobRole'
    },
    {
      name: 'ownership',
      displayName: 'Ownership',
      isSelected: true,
      canConfigure: false,
      subSections: [
        {
          name: 'ownershipSharesReportedDate',
          displayName: 'Shares, Reported Date',
          isSelected: true,
          canConfigure: true,
          unit: 1
        },
        {
          name: 'ownershipShareChange',
          displayName: 'Share Change',
          isSelected: false,
          canConfigure: true,
          unit: 1
        },
        {
          name: 'itineraryOwnershipHolderRank',
          displayName: 'Holder Rank',
          isSelected: false,
          canConfigure: true,
          unit: 1
        },
        {
          name: 'ownershipValue',
          displayName: 'ownershipValue',
          isSelected: true,
          canConfigure: true,
          unit: 1
        },
        {
          name: 'ownershipShareValueChange',
          displayName: 'ownershipShareValueChange',
          isSelected: false,
          canConfigure: true,
          unit: 1
        },
        {
          name: 'ownershipPctSO',
          displayName: '% S/O',
          isSelected: false,
          canConfigure: true,
          unit: 0.5
        },
        {
          name: 'ownershipHolderPctPortfolio',
          displayName: '% Portfolio',
          isSelected: false,
          canConfigure: true,
          unit: 0.5
        }
      ],
      canReorderSubSections: true
    },
    {
      name: 'institutionDetails',
      displayName: 'Institution Details',
      isSelected: true,
      canConfigure: false,
      subSections: [
        {
          name: 'institutionDetailsOrientation',
          displayName: 'Orientation',
          isSelected: false,
          canConfigure: true,
          unit: 1
        },
        {
          name: 'institutionDetailsStyle',
          displayName: 'Style',
          isSelected: false,
          canConfigure: true,
          unit: 1
        },
        {
          name: 'institutionDetailsTurnover',
          displayName: 'Equity Portfolio Turnover',
          isSelected: false,
          canConfigure: true,
          unit: 1
        },
        {
          name: 'itineraryInstitutionDetailsEquityAssets',
          displayName: 'itineraryInstitutionDetailsEquityAssets',
          isSelected: false,
          canConfigure: true,
          unit: 1
        },
        {
          name: 'institutionDetailsCity',
          displayName: 'City',
          isSelected: false,
          canConfigure: true,
          unit: 1
        },
        {
          name: 'institutionDetailsCountry',
          displayName: 'Country/Territory',
          isSelected: false,
          canConfigure: true,
          unit: 1
        }
      ],
      canReorderSubSections: true
    },
    {
      name: 'targeting',
      displayName: 'Targeting',
      isSelected: true,
      canConfigure: false,
      subSections: [
        {
          name: 'targetingInstitutionRating',
          displayName: 'Institution Rating',
          isSelected: false,
          canConfigure: true,
          unit: 0.5
        },
        {
          name: 'targetingSuitabilityScore',
          displayName: 'Suitability Score',
          isSelected: true,
          canConfigure: true,
          unit: 0.5
        },
        {
          name: 'targetingPurchasingPower',
          displayName: 'targetingPurchasingPowerUsd',
          isSelected: false,
          canConfigure: true,
          unit: 1
        },
        {
          name: 'targetingBestFund',
          displayName: 'Best Fund',
          isSelected: false,
          canConfigure: true,
          unit: 2
        }
      ],
      canReorderSubSections: true
    },
    {
      name: 'itineraryActivities',
      displayName: 'Activities',
      isSelected: true,
      canConfigure: false,
      subSections: [
        {
          name: 'lastDateMetWithInstitution',
          displayName: 'Last Date Met with Institution',
          isSelected: true,
          canConfigure: true,
          unit: 1
        },
        {
          name: 'activitiesWithInstitution',
          displayName: '# Activities with Institution',
          isSelected: true,
          canConfigure: true,
          unit: 1
        }
      ],
      canReorderSubSections: true
    }
  ];

  getDisabledSectionsMap(): Map<string, boolean> {
    const disabledMap = new Map<string, boolean>();
    const currentWeight = this.calculateTotalSelectedUnits();

    this.availableDataSections.forEach(section => {
      const isDisabled = this.shouldDisableDataSection(section, currentWeight);
      disabledMap.set(section.name, isDisabled);

      if (section.subSections) {
        section.subSections.forEach(subSection => {
          const subIsDisabled = this.shouldDisableSubSection(subSection, currentWeight);
          disabledMap.set(subSection.name, subIsDisabled);
        });
      }
    });

    return disabledMap;
  }
}

export interface participantInfo {
  participants: ItineraryParticipantInfo[];
}
export interface ItineraryParticipantInfoData {
  [ReportViewModelSection.Itinerary_ParticipantInfo]: participantInfo;
  [ReportViewModelSection.Itinerary_ActivityHeader]: ItineraryActivity;
}
